'use client';

import { faCalendar, faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import getCDNURL from '@pickleballinc/ui/components/global/utils/getCDNURL';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useParams } from 'next/navigation';

import { Button } from '@/components/atoms';
import { BANNER_TYPE, NextEventGameday } from '@/entities/NextEventGameday';
import { DateUtil } from '@/utils/date';

interface Props {
	event: NextEventGameday;
}
export const NextGamedayOrEvent = ({ event }: Props) => {
	return <>{event.bannerType === BANNER_TYPE.SCHEDULE_EVENT ? <NextScheduledEvent event={event} /> : <NextGameday event={event} />}</>;
};

const NextScheduledEvent = ({ event }: { event: NextEventGameday }) => {
	const params = useParams<{
		teamLeagueId: string;
	}>();
	const eventHeader = determineHeaderLabel(event.startDate, 'event');
	const abbreviation = event.timezone.abbreviation;
	const gamedayStartDate = DateUtil.formatUTCInTimezone(event.startDate, 'MMM D, YYYY | hh:mm A', event.timezone.ianaTzIdentifier);

	const locationParts = [
		event.location?.venue,
		event.location?.streetAddress,
		event.location?.city,
		event.location?.stateInfo?.abbreviation
	].filter(Boolean);

	return (
		<Link href={`/team-leagues/${params.teamLeagueId}/event/${event.uuid}`} prefetch={false} className="cursor-pointer group">
			<div className="flex flex-col md:flex-row md:justify-between md:items-center rounded-lg border border-orange-500 p-3 h-full gap-2.5 md:gap-0">
				<p className="text-gray-900 font-bold text-center md:text-start md:hidden">{eventHeader}</p>
				<p className="text-sm font-medium text-gray-900 md:hidden">{event.title}</p>
				<div className="flex gap-2 md:gap-4 items-center">
					{event.logo ? (
						<img
							src={getCDNURL(event.logo.url, 400, 200)}
							alt={event.logo?.uuid}
							className="w-32 h-20 object-contain rounded-lg border md:w-40 md:h-24"
						/>
					) : null}
					<div className="flex flex-col gap-1.5">
						<p className="text-sm text-gray-900 font-normal hidden md:block">
							{event.title} - {eventHeader}
						</p>
						<p className="text-gray-700 text-sm font-normal flex gap-1.5">
							<FontAwesomeIcon icon={faCalendar} className="size-4" />
							{gamedayStartDate} {abbreviation}
						</p>

						{locationParts.length > 0 && (
							<p className="text-gray-700 text-sm font-normal flex gap-1.5">
								<FontAwesomeIcon icon={faMapMarkerAlt} className="size-4" />
								{locationParts.join(', ')}
							</p>
						)}
					</div>
				</div>
				<p className="text-orange-500 text-sm font-semibold cursor-pointer text-center group-hover:text-darkorange-600">
					Go to {event.title}
				</p>
			</div>
		</Link>
	);
};

const determineHeaderLabel = (startDate: string, type: 'event' | 'gameday'): string => {
	const now = dayjs().utc().format();
	const diffInHours = dayjs(startDate).diff(now, 'hour', true);
	if (diffInHours < 24 && diffInHours > 0) {
		return type === 'gameday' ? 'Game Day Starting Soon!' : 'Starting Soon!';
	} else if (diffInHours >= 24) {
		return type === 'gameday' ? 'Upcoming Game Day!' : 'Upcoming Event!';
	}
	return type === 'gameday' ? 'Game Day is in Progress!' : 'In Progress!';
};

const NextGameday = ({ event }: { event: NextEventGameday }) => {
	const params = useParams<{
		teamLeagueId: string;
	}>();
	const gamedayHeader = determineHeaderLabel(event.startDate, 'gameday');
	const abbreviation = event.timezone.abbreviation;
	const gamedayStartDate = DateUtil.formatUTCInTimezone(event.startDate, 'MMM D, YYYY | hh:mm A', event.timezone.ianaTzIdentifier);
	const startDate = dayjs.utc(event.startDate).tz(event.timezone.ianaTzIdentifier).format('YYYY-MM-DD');
	const endDate = dayjs.utc(event.startDate).tz(event.timezone.ianaTzIdentifier).add(1, 'day').format('YYYY-MM-DD');

	return (
		<Link
			href={`/team-leagues/${params.teamLeagueId}/gameday?startDate=${startDate}&endDate=${endDate}`}
			prefetch={false}
			className="cursor-pointer group"
		>
			<div className="bg-orange-50 p-3 flex flex-col md:flex-row md:justify-between gap-2 rounded-lg border border-orange-400 md:items-center">
				<div className="flex flex-col gap-1.5">
					<p className="text-gray-900 font-bold text-center md:text-start">{gamedayHeader}</p>
					<div className="flex flex-col md:flex-row md:gap-2">
						<p className="text-gray-700 text-sm font-normal flex gap-1.5">
							<FontAwesomeIcon icon={faCalendar} className="size-4 md:hidden" />
							{gamedayStartDate} {abbreviation}
						</p>
						{event.location?.venue ? (
							<p className="text-gray-700 text-sm font-normal flex gap-1.5">
								<span className="hidden md:block">|</span>
								<FontAwesomeIcon icon={faMapMarkerAlt} className="size-4 md:hidden" />
								{event.location.venue}
							</p>
						) : null}
					</div>
				</div>
				<Button className="!h-8 flex w-full items-center justify-center gap-1 text-sm font-semibold sm:w-auto border border-gray-300 !bg-white text-orange-500 transition-all duration-200 shadow-xs group-hover:border-gray-400 group-hover:bg-gray-50 group-hover:shadow-sm group-hover:text-darkorange-600">
					Go to Game Day
				</Button>
			</div>
		</Link>
	);
};
